import { ReactNode } from 'react'
import { LootBoxDetailCardDto, LootBoxDetailCardLineItemDto } from '../../api/loot-boxes/loot-tables';

// ----------------------------------------------------------------------
// Mini-markup parser
// ----------------------------------------------------------------------
function renderFormatted(text: string): ReactNode[] {
  const pieces: ReactNode[] = []
  const tagRegex = /<\/?[biu]>/g

  // Keep track of via stack which tags are currently active.
  const stack: ('b' | 'i' | 'u')[] = []
  let lastIndex = 0
  let key = 0

  const activeClasses = () =>
  [
    stack.includes('b') && 'font-bold',
    stack.includes('i') && 'italic',
    stack.includes('u') && 'underline',
  ]
    .filter(Boolean)
    .join(' ')

  for (let match; (match = tagRegex.exec(text)); ) {
    const [tag] = match
    const plaintext = text.slice(lastIndex, match.index)
    // Bit inefficient, but effective.
    if (plaintext) {
      pieces.push(
        <span key={key++} className={activeClasses()}>
          {plaintext}
        </span>,
      )
    }

    // Update this type assertion if you update tagRegex.
    const name = tag.replace(/[<\/>]/g, '') as 'b' | 'i' | 'u'
    if (tag.startsWith('</')) {
      // Closing tag - pop last occurrence of this name.
      const index = stack.lastIndexOf(name)
      if (index !== -1) stack.splice(index, 1)
    } else {
      // Opening tag - push onto stack.
      stack.push(name)
    }

    lastIndex = tagRegex.lastIndex
  }

  // Trailing text after the last tag.
  const tail = text.slice(lastIndex)
  if (tail) {
    pieces.push(
      <span key={key++} className={activeClasses()}>
        {tail}
      </span>,
    )
  }

  return pieces
} 

type Props = {
  detailCard: LootBoxDetailCardDto
}

export default function LootBoxDetailCard({ detailCard }: Props) {
  const getAlignmentClass = (align: LootBoxDetailCardLineItemDto['align']) => {
    switch (align) {
      case 'center':
        return 'text-center'
      case 'left':
        return 'text-left'
      case 'right':
        return 'text-right'
    }
  }

  // Use the background color if provided, otherwise use this default.
  const backgroundColor = detailCard.bgColorHex ?? '##1F2937' // gray-800

  return (
    <div
      className='rounded-md border border-gray-700 overflow-hidden shadow-xl'
      style={{ backgroundColor }}
    >
      {/* Card Header */}
      <div className='px-4 py-3 border-b border-gray-700 bg-black bg-opacity-20'>
        <h4 className='text-sm font-semibold text-white text-center'>
          {detailCard.title}
        </h4>
      </div>

      {/* Card Body */}
      <div className='px-4 py-3 space-y-1'>
        {detailCard.lineItems.map((item, index) => (
          <div
            key={index}
            className={`text-xs text-gray-200 ${getAlignmentClass(item.align)}`}
          >
            <p className='leading-relaxed'>{renderFormatted(item.body)}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

